import { useMemo, useState } from "react";
import { DateTimeRenderer } from "../common/datetime/DateTimeFormatter";
import { LucideIcon } from "../common/icon/LucideIcon";
import { ColumnsSettings } from "../common/tables/ColumnsSelector/ColumnsSelector";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { AuditLog, AuditLogFilters, AuditLogSearchTermOptions } from "../api/AuditLog";
import { ResourceName } from "../main/Routing";
import { Modal, ModalBody } from "react-bootstrap";
import { LoadingWrapper } from "../common/LoadingWrapper";
import { EntityTable } from "../common/tables/EntityTable/EntityTable";
import { eventIdToInfo, getAuditLogDefaultMessage } from "./AuditLog";
import { EntityFilterIndicator } from "../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { AuditLogFilterBar, AuditLogFilterForm } from "./AuditLogFilterBar";
import { NotAvailable, NotSet } from "../common/misc/UIconstants";
import { TableTabsDict } from "../common/tables/Tabs/TableTabsTypes";
import { useTabStore } from "../common/tables/Tabs/useTabStore";
import TableView, { TableViewLabel } from "../common/panels/TableView/TableView";
import { EntityTableTabs } from "../common/tables/Tabs/EntityTableTabs";
import { GetPersons, TableArrayRenderer } from "../common/misc/EntityRenders/EntityRenderer";
import { toUppercase } from "../common/helperfunctions/stringFunctions";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableColumns,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../common/entity/EntityInterfaces";
import { genericEntityConstants } from "../api/GenericConstants";
import SearchInputWithOptions from "../common/forms/SearchInputWithOptions/SearchInputWithOptions";
import { EntityOrderBy, InferSortByOptions } from "../api/GenericTypes";
import { MoreDropdown } from "../common/buttons/MoreDropdown/MoreDropdown";
import { ExportCsvButton } from "../common/tables/ExportCsvButton/ExportCsvButton";
import { personsConstants } from "../api/Person";

interface ModalTarget {
  resource: ResourceName;
  row: AuditLog;
  component?: JSX.Element;
}

export const switchAuditLogDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<AuditLogFilters>>[] | null | undefined
): SortState<InferSortByOptions<AuditLogFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "TIMESTAMP":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-timestamp",
          property: sort.property,
        };

      case "EVENT_ID":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-event-id",
          property: sort.property,
        };

      case "USER":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-person",
          property: sort.property,
        };

      case "ENTITY_TYPE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-entity-type",
          property: sort.property,
        };

      default:
        return {
          sortDirection: "DESC",
          headerId: "default-timestamp",
          property: "TIMESTAMP",
        };
    }
  });
};

export const defaultAuditLogsFilters: AuditLogFilters = {
  searchTermIncludeNames: true,
  searchTermIncludeIds: true,
  sortBy: [
    {
      property: "TIMESTAMP",
      sortDirection: "DESC",
    },
  ],
};

export const useAuditLogsTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"auditLogs">) => {
  const defaults: ColumnsSettings<AuditLog> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-timestamp": { pos: 1, active: true, header: fieldLabels.timestamp, property: "timestamp" },
      "default-event-id": { pos: 2, active: true, header: fieldLabels.eventId, property: "eventId" },
      "default-event-message": { pos: 3, active: true, header: fieldLabels.message, property: "message" },
      "default-person": { pos: 4, active: true, header: fieldLabels.user, property: "user" },
      "default-entity-type": { pos: 5, active: true, header: fieldLabels.entityType, property: "entityType" },
      "default-entity-records": {
        pos: 6,
        active: true,
        header: fieldLabels.entityIntRecords,
        property: "entityIntRecords",
      },
      "default-api-key": { pos: 9, active: false, header: fieldLabels.apiKey, property: "apiKey" },
      "default-metadata-mapping": {
        pos: 10,
        active: false,
        header: fieldLabels.metadataMapping,
        property: "metadataMapping",
      },
    }),
    [fieldLabels]
  );
  const tabStoreDefaults: TableTabsDict<AuditLog, AuditLogFilters, AuditLogFilterForm> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultAuditLogsFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
    }),
    []
  );

  return { defaults, tabStoreDefaults };
};

export const useAuditLogsTableColumns: UseEntityTableColumns<"auditLogs"> = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"auditLogs">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<AuditLog> = [
      {
        Header: fieldLabels.id,
        id: "default-id",
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 100,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.timestamp,
        id: "default-timestamp",
        accessor: (row) => (
          <div className="flex align-center gap-5" style={{ flexFlow: "row nowrap", overflow: "hidden" }}>
            <div>
              <LucideIcon name={entityConstants.icon} color="var(--primary)" />
            </div>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <DateTimeRenderer date={row.timestamp} includeElapsed />
            </div>
          </div>
        ),
        align: "left",
        width: 280,
        minWidth: 100,
        ...getAsyncSortingFn("TIMESTAMP", sort, setSort),
      },
      {
        Header: fieldLabels.eventId,
        id: "default-event-id",
        accessor: (row) => {
          const rowInfo = eventIdToInfo(row.eventId);
          return (
            <div
              className={"label label-soft-" + rowInfo.color}
              style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}
            >
              <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                  {rowInfo?.icon && <LucideIcon name={rowInfo.icon} />} {rowInfo.label}
                </span>
              </div>
            </div>
          );
        },
        align: "left",
        width: 180,
        minWidth: 100,
        ...getAsyncSortingFn("EVENT_ID", sort, setSort),
      },
      {
        Header: fieldLabels.message,
        id: "default-event-message",
        accessor: (row) => {
          const { entityConstants } =
            row.entityType && Object.hasOwn(genericEntityConstants, row.entityType)
              ? genericEntityConstants[row.entityType]
              : { entityConstants: undefined };
          const rowMessage = row.message ?? getAuditLogDefaultMessage(row);
          return (
            <div title={rowMessage} style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}>
              {entityConstants?.icon && (
                <div>
                  <LucideIcon name={entityConstants.icon} color="var(--primary)" />
                </div>
              )}
              <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>{rowMessage}</span>
              </div>
            </div>
          );
        },
        align: "left",
        width: 400,
        minWidth: 100,
      },
      {
        Header: fieldLabels.user,
        id: "default-person",
        accessor: (row) =>
          !!row.user ? (
            !!row.user.name ? (
              <div className="flex align-center gap-5" style={{ width: "100%" }}>
                <GetPersons persons={[row.user]} createLinks={false} />
              </div>
            ) : (
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <LucideIcon name="user" color="var(--primary)" />{" "}
                <span>{`Deleted ${personsConstants.entitySingular} - ID: ${row.user.id}`}</span>
              </div>
            )
          ) : (
            NotSet
          ),
        align: "left",
        width: 200,
        minWidth: 100,
        ...getAsyncSortingFn("USER", sort, setSort),
      },
      {
        Header: fieldLabels.apiKey,
        id: "default-api-key",
        accessor: (row) => (
          <div style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                {row.apiKey?.name || NotSet}
              </span>
            </div>
          </div>
        ),
        align: "left",
        width: 200,
        minWidth: 100,
      },
      {
        Header: fieldLabels.metadataMapping,
        id: "default-metadata-mapping",
        accessor: (row) => (
          <div style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                {row.metadataMapping?.name || NotSet}
              </span>
            </div>
          </div>
        ),
        align: "left",
        width: 200,
        minWidth: 100,
      },
      {
        Header: fieldLabels.entityType,
        id: "default-entity-type",
        accessor: (row) => {
          if (!row.entityType) return NotSet;
          if (!Object.hasOwn(genericEntityConstants, row.entityType))
            return (
              <div style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}>
                <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                    <LucideIcon name="triangle-alert" color="var(--warning)" />
                    <span> Unknown content type</span>
                  </span>
                </div>
              </div>
            );
          const { entityConstants } = genericEntityConstants[row.entityType];
          return !!entityConstants ? (
            <div style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                  <LucideIcon name={entityConstants.icon} color="var(--primary)" />
                  <span> {toUppercase(entityConstants.entitySingular)}</span>
                </span>
              </div>
            </div>
          ) : (
            NotAvailable
          );
        },
        align: "left",
        width: 200,
        minWidth: 100,
        ...getAsyncSortingFn("ENTITY_TYPE", sort, setSort),
      },
      {
        Header: fieldLabels.entityIntRecords,
        id: "default-entity-records",
        accessor: (row) => {
          var records: AuditLog["entityStringRecords"] = [];
          if (!!row.entityIntRecords?.length)
            records = row.entityIntRecords.map((r) => ({ ...r, id: r.id.toString() }));
          else if (!!row.entityStringRecords?.length) records = row.entityStringRecords;
          else if (!!row.entityLongRecords?.length)
            records = row.entityLongRecords.map((r) => ({ ...r, id: r.id.toString() }));
          return !!records.length ? (
            <TableArrayRenderer values={records}>
              {(value) => (
                <div
                  style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  title={!!value.name ? `${value.name} - ID: ${value.id}` : `ID: ${value.id}`}
                >
                  {!!value.name ? `${value.name} - ID: ${value.id}` : `ID: ${value.id}`}
                </div>
              )}
            </TableArrayRenderer>
          ) : (
            NotSet
          );
        },
        align: "left",
        width: 400,
        minWidth: 100,
      },
    ];

    return headers;
  }, [
    entityConstants.icon,
    fieldLabels.apiKey,
    fieldLabels.entityIntRecords,
    fieldLabels.entityType,
    fieldLabels.eventId,
    fieldLabels.id,
    fieldLabels.message,
    fieldLabels.metadataMapping,
    fieldLabels.timestamp,
    fieldLabels.user,
    setSort,
    sort,
  ]);
  return { columns };
};
export const AuditLogTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
}: EntityTableProps<"auditLogs">) => {
  const [modalTarget, setModalTarget] = useState<ModalTarget>();

  const { defaults, tabStoreDefaults } = useAuditLogsTableDefaults({ fieldLabels });

  const { selection, resultsCount, onCountChange, onSelectionChange } = useGenericVirtualizedTable<AuditLog>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<AuditLog, AuditLogFilters, AuditLogFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
    enable: false,
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue, onTabChange, debouncedSearchValue } =
    useGenericVirtualizedTableTabs({
      currentTab,
      tabsLoading,
      filters,
      switchSortState: switchAuditLogDefaultSortState,
      dispatchTabStore,
    });

  const { columns } = useAuditLogsTableColumns({ entityConstants, fieldLabels, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);
  // console.log("Filters", filters, "Memoized Filters", memoizedFilters);
  return (
    <>
      <TableView>
        <TableView.Head>
          <TableView.Head.Label>
            <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
          </TableView.Head.Label>
        </TableView.Head>
        <TableView.Body>
          <TableView.Body.Sidebar>
            <AuditLogFilterBar
              initialValues={sidebarFilters}
              dispatchTabStore={dispatchTabStore}
              tabsLoading={tabsLoading}
              currentTab={currentTab}
            />
          </TableView.Body.Sidebar>
          <TableView.Body.Content>
            <EntityTableTabs
              currentTab={currentTab}
              onTabChange={onTabChange}
              temporaryTabs={temporaryTabs}
              fixedTabs={fixedTabs}
              filters={filters}
              sidebarFilters={sidebarFilters}
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              customTabs={customTabs}
              dispatchTabStore={dispatchTabStore}
              tabsLoading={tabsLoading}
              tabsModified={tabsModified}
              showAddTab={false}
            />
            <EntityTable>
              <EntityTable.Controls
                style={{
                  borderTop: "0px",
                  borderRadius: "0px",
                }}
              >
                <EntityFilterIndicator<AuditLog, AuditLogFilters>
                  filters={filters}
                  excludeFilters={{
                    searchTermIncludeIds: () => false,
                    searchTermIncludeNames: () => false,
                    searchTermIncludeUuids: () => false,
                  }}
                />

                <SearchInputWithOptions
                  searchTermOptions={AuditLogSearchTermOptions}
                  filters={filters}
                  setFilters={async (newFilters) => {
                    await dispatchTabStore({
                      type: "setTab",
                      options: { keepPrevious: true },
                      payload: { settings: { filters: { searchTerm: debouncedSearchValue, ...newFilters } } },
                    });
                  }}
                  searchValue={searchValue}
                  setSearchValue={setSearchValue}
                />
                <div
                  className="flex row-nowrap align-center gap-5"
                  style={{ height: "min-content", marginBottom: "auto" }}
                >
                  <MoreDropdown drop="right" btn="btn btn-ghost-secondary" contentStyle={{ padding: "2px" }}>
                    <li>
                      <ExportCsvButton
                        entityConstants={entityConstants}
                        columnSettings={columnSetting}
                        filters={{
                          ids: selection.size > 0 ? Array.from(selection) : undefined,
                          ...memoizedFilters,
                          searchTerm: debouncedSearchValue,
                        }}
                        disabled={selection.size === 0}
                      />
                    </li>
                  </MoreDropdown>
                </div>
              </EntityTable.Controls>
              <EntityTable.Body<AuditLog, AuditLogFilters>
                functionRef={functionRef}
                entityConstants={entityConstants}
                filters={memoizedFilters}
                columns={columns}
                columnSelect
                columnSetting={columnSetting}
                columnWidths={columnWidths}
                defaultColumnSettings={defaults}
                dispatchTabStore={dispatchTabStore}
                setResultsCount={onCountChange}
                onSelectionChange={onSelectionChange}
                loading={tabsLoading}
                disablePermissionQuery={true}
              />
            </EntityTable>
          </TableView.Body.Content>
        </TableView.Body>
      </TableView>

      <Modal bsSize="lg" show={modalTarget?.row !== undefined} onHide={() => setModalTarget(undefined)} target="body">
        <ModalBody>
          <LoadingWrapper
            status={!modalTarget?.component ? "loading" : "success"}
            fetchStatus={!modalTarget?.component ? "fetching" : "idle"}
          >
            {modalTarget?.component}
          </LoadingWrapper>
        </ModalBody>
      </Modal>
    </>
  );
};
